import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * useProductFilters : gestion des filtres synchronisés avec l'URL (search params)
 */
export const useFilterParam = filterFields => {
	const [searchParams, setSearchParams] = useSearchParams();

	// Transforme searchParams => { field: [val1, val2, ...] }
	const paramsToFilters = useCallback(
		params => {
			const filters = {};
			filterFields.forEach(field => {
				const value = params.get(field);
				filters[field] = value ? value.split(",") : [];
			});

			return filters;
		},
		[filterFields]
	);

	// Transforme { field: [val1, val2] } => URLSearchParams (en conservant les autres params !)
	const filtersToParams = useCallback(
		filters => {
			const params = new URLSearchParams(searchParams.toString());
			filterFields.forEach(field => {
				if (filters[field] && filters[field].length) {
					params.set(field, filters[field].join(","));
				} else {
					params.delete(field);
				}
			});
			return params;
		},
		[searchParams, filterFields]
	);

	const filters = paramsToFilters(searchParams);

	const setFilters = useCallback(
		newFilters => {
			setSearchParams(filtersToParams(newFilters));
		},
		[setSearchParams, filtersToParams]
	);

	const setFilterValue = useCallback(
		(field, value) => {
			if (!value || value.length === 0) {
				// eslint-disable-next-line no-unused-vars
				const { [field]: _, ...rest } = filters;
				setFilters(rest);
				return;
			}

			setFilters({ ...filters, [field]: [value] });
		},
		[filters, setFilters]
	);

	const removeFilterValue = useCallback(
		(field, valueToRemove) => {
			const current = filters[field] || [];
			const next = current.filter(v => v !== valueToRemove);
			const nextFilters = { ...filters, [field]: next };
			if (!next.length) {
				delete nextFilters[field];
			}
			setFilters(nextFilters);
		},
		[filters, setFilters]
	);

	const resetFilters = useCallback(() => {
		const params = new URLSearchParams(searchParams.toString());
		filterFields.forEach(field => params.delete(field));
		setSearchParams(params);
	}, [searchParams, setSearchParams, filterFields]);

	const selectedFiltersCount = Object.values(filters).reduce(
		(acc, arr) => acc + (arr?.length || 0),
		0
	);

	return {
		filters,
		setFilters,
		setFilterValue,
		removeFilterValue,
		resetFilters,
		selectedFiltersCount,
	};
};
